// import { sessionURL } from 'logrocket';
import { useLocationPathname } from '@rocketlane/rocket-router';
import { matchPath } from '@rocketlane/rocket-router';
import { useCallback, useEffect, useRef } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { AccountAPI } from 'api/account';
import { RoutePaths } from 'constant';
import { InitRouter } from 'initRouter';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { isHippaAccount } from 'utils/is-hippa-account';
import { isNitroEnvironment } from 'utils/is-nitro-env';
import {
  getSessionReplayLink,
  shouldInitDatadogRum,
} from 'vendor/datadog-rum/rum-loader';
import { useAccount } from './use-account';
import { useUser } from './use-user';

declare global {
  interface Window {
    isAppLoaded: boolean;
  }
}

const TABS_OPEN = 'tabs_open.count';

export enum RouteLevelEventTags {
  ProjectConversationPageLoad = 'LOAD: project-conversations:page-load',
  ProjectConversationRouteChange = 'ROUTE-CHANGE: project-conversations:route-change',
  TimesheetsPageLoad = 'LOAD: timesheets:page-load',
  TimesheetsRouteChange = 'ROUTE-CHANGE: timesheets:route-change',
  TasksListPageLoad = 'LOAD: tasks-list:page-load',
  TasksListRouteChange = 'ROUTE-CHANGE: tasks-list:route-change',
  ProjectTasksListPageLoad = 'LOAD: project-tasks-list:page-load',
  ProjectTasksListRouteChange = 'ROUTE-CHANGE: project-tasks-list:route-change',
  TimesheetReportsPageLoad = 'LOAD: timesheets-report:page-load',
  TimesheetReportsRouteChange = 'ROUTE-CHANGE: timesheets-report:route-change',
  ProjectPlanPageLoad = 'LOAD: project-plan:page-load',
  ProjectPlanRouteChange = 'ROUTE-CHANGE: project-plan:route-change',
  ProjectListPageLoad = 'LOAD: projects-list:page-load',
  ProjectListRouteChange = 'ROUTE-CHANGE: projects-list:route-change',
  CreateNewProjectModal = 'LOAD: project-list:create-new-project',
  CreateNewProjectModalRouteChange = 'ROUTE-CHANGE: project-list:create-new-project',
  OverviewPageLoad = 'LOAD: project-overview:page-load',
  OverviewRouteChange = 'ROUTE-CHANGE: project-overview:route-change',
  ResourceManagementLoad = 'LOAD: resource-management:page-load',
  ResourceManagementRouteChange = 'ROUTE-CHANGE: resource-management:route-change',
  HomePageLoad = 'LOAD: home:page-load',
  HomePageRouteChange = 'ROUTE-CHANGE: home:route-change',
  TimesheetsSingleUserPageLoad = 'LOAD: timesheets-single-user-approval:page-load',
  TimesheetsSingleUserRouteChange = 'ROUTE-CHANGE: timesheets-single-user-approval:route-change',
}

export enum ClickEventTags {
  ProjectPlanTaskLoad = 'LOAD: project-plan:task-load',
  AllTasksTaskLoad = 'LOAD: all-tasks:task-load',
  CommentPublished = 'ACTION: conversations:post-comment',
  CkMentionPopup = 'ACTION: chat:ck-mention-popup',
  AssigneePickerPopup = 'ACTION: assignee-picker-popup',
  TimesheetProjectPopupOpen = 'ACTION: timesheet:project-popup',
  TimesheetTaskPopupOpen = 'ACTION: timesheet:task-popup',
  TimesheetCalendarDrawerOpen = 'ACTION: timesheet:calendar-drawer-open',
  TimesheetAddPreviousRows = 'ACTION: timesheet:add-last-week-rows',
  TimesheetTimeEntryPopupOpen = 'ACTION: timesheet:time-entry-popup',
  DueDatePickerPopup = 'ACTION: due-date-picker-popup',
  ProjectPlanTaskCreate = 'ACTION: project-plan:task-create',
  ProjectPlanInviteMemberPopup = 'ACTION: project-plan:invite-member-popup',
  ProjectPlanPlaceholderResolutionPopup = 'ACTION: project-plan:placeholder-resolution-popup',
  TaskPaneTimeEntryDrawerOpen = 'ACTION: task-pane:time-entry-drawer',
  ProjectTaskListFilterChange = 'ACTION: projects-tasks-list:filter-change',
  FieldEdit = 'ACTION: field:value-change',
  HomeTaskLoad = 'LOAD: home:task-load',
}

enum AccountGroupingBasedOnSize {
  Smb = 'Smb',
  MidMarket = 'MidMarket',
  Enterprise = 'Enterprise',
}

// add a tag here if you want to log the duration of the events starting from the app load.
// markers for the tags of a particular route will be automatically set if the route changes

export const RouteBasedTagsMap = {
  [RoutePaths.myTimesheets]: [
    RouteLevelEventTags.TimesheetsPageLoad,
    RouteLevelEventTags.TimesheetsRouteChange,
  ],
  [RoutePaths.timesheets]: [
    RouteLevelEventTags.TimesheetsPageLoad,
    RouteLevelEventTags.TimesheetsRouteChange,
  ],
  [RoutePaths.projectConversationDetails]: [
    RouteLevelEventTags.ProjectConversationPageLoad,
    RouteLevelEventTags.ProjectConversationRouteChange,
  ],
  [RoutePaths.tasks]: [
    RouteLevelEventTags.TasksListPageLoad,
    RouteLevelEventTags.TasksListRouteChange,
  ],
  [RoutePaths.projectTaskList]: [
    RouteLevelEventTags.ProjectTasksListPageLoad,
    RouteLevelEventTags.ProjectTasksListRouteChange,
  ],
  [RoutePaths.timesheetReports]: [
    RouteLevelEventTags.TimesheetReportsPageLoad,
    RouteLevelEventTags.TimesheetReportsRouteChange,
  ],
  [RoutePaths.projectPlan]: [
    RouteLevelEventTags.ProjectPlanPageLoad,
    RouteLevelEventTags.ProjectPlanRouteChange,
  ],
  [RoutePaths.projects]: [
    RouteLevelEventTags.ProjectListPageLoad,
    RouteLevelEventTags.ProjectListRouteChange,
  ],
  [RoutePaths.projectOverview]: [
    RouteLevelEventTags.OverviewPageLoad,
    RouteLevelEventTags.OverviewRouteChange,
  ],
  [RoutePaths.resourceManagement]: [
    RouteLevelEventTags.ResourceManagementLoad,
    RouteLevelEventTags.ResourceManagementRouteChange,
  ],
  [RoutePaths.home]: [
    RouteLevelEventTags.HomePageLoad,
    RouteLevelEventTags.HomePageRouteChange,
  ],
  [RoutePaths.timesheetSingleUserApproval]: [
    RouteLevelEventTags.TimesheetsSingleUserPageLoad,
    RouteLevelEventTags.TimesheetsSingleUserRouteChange,
  ],
  // make sure to keep create before detail, or else it will not be calculated properly as both have similar route structure
  [RoutePaths.projectCreate]: [
    RouteLevelEventTags.CreateNewProjectModal,
    RouteLevelEventTags.CreateNewProjectModalRouteChange,
  ],
  [RoutePaths.projectDetail]: [
    RouteLevelEventTags.ProjectPlanPageLoad,
    RouteLevelEventTags.ProjectPlanRouteChange,
  ],
};

// adding this here because changing route from timeline to plan causes end marker to get set first and then the start marker, which causes discrepancy
// I know it's a hacky solution but what to do
const IgnoreRouteChangeForThese = {
  [RoutePaths.projectPlanTimeline]: RoutePaths.projectPlan,
};

const RedirectedRoutes = {
  [RoutePaths.timesheets]: [
    RoutePaths.myTimesheets,
    RoutePaths.timesheetsApproval,
  ],
  [RoutePaths.projectDetail]: [RoutePaths.projectPlan],
};

export enum LogType {
  AutoLog = 'AUTO',
  CallbackLog = 'MANUAL',
}

const logVoid = (_tagNames: string[]) => {};

export const setMarkerForDataDog = (markerName: string): void => {
  if (window.isAppLoaded) {
    performance.clearMarks(markerName);
    performance.mark(markerName);
  }
};

const clearMarksForDataDog = () => {
  performance.clearMeasures();
  performance.clearMarks();
};

export const useLoadLoggerForDataDog = (
  tagNames: string[],
  logType: LogType = LogType.AutoLog,
  trackVisits = false,
): { executeLogger: () => void } => {
  const account = useAccount();
  const { user } = useUser();
  const pathname = useLocationPathname();
  const { enter } = InitRouter.getInstance();
  let accountGroup;
  const userCount = account.metaData?.userCount ?? 0;

  if (userCount >= 100) {
    accountGroup = AccountGroupingBasedOnSize.Enterprise;
  } else if (userCount >= 30 && userCount < 100) {
    accountGroup = AccountGroupingBasedOnSize.MidMarket;
  } else {
    accountGroup = AccountGroupingBasedOnSize.Smb;
  }

  const logEntry = useCallback(
    (_tagNames: string[]) => {
      if (!window.isAppLoaded) {
        window.isAppLoaded = true;
      } else {
        _tagNames.forEach((tagName) => {
          try {
            const markerEntry = performance.getEntriesByName(tagName)[0];

            if (markerEntry) {
              const loadMeasure = performance.measure(tagName, tagName);
              const loadTime = Math.floor(loadMeasure.duration);

              const sendPerformanceLog = (rumUrl: string) => {
                const logData = {
                  type: tagName,
                  userId: user.userId,
                  name: isHippaAccount() ? '' : fullName(user),
                  userType: user.userType,
                  account: account.defaultDomainName,
                  url: window.location.pathname,
                  loadTime,
                  rumUrl,
                  build: isNitroEnvironment() ? 'staging' : 'production',
                  accountStatus: account.status,
                  accountGroup,
                  ...('connection' in navigator
                    ? {
                        connectionInfo: `rtt: ${
                          (navigator as any).connection.rtt
                        }, downlink: ${
                          (navigator as any).connection.downlink
                        }, effectiveType: ${
                          (navigator as any).connection.effectiveType
                        }, saveData: ${(navigator as any).connection.saveData}`,
                      }
                    : {}),
                  ...('memory' in performance
                    ? {
                        usedJSHeapSizeMb:
                          ((performance as any)?.memory?.usedJSHeapSize ?? 0) /
                          (1024 * 1024),
                      }
                    : {}),
                  ...('deviceMemory' in navigator
                    ? { memorySizeGb: (navigator as any).deviceMemory }
                    : {}),
                  hardwareConcurrency: navigator.hardwareConcurrency,
                  tabsOpenCount:
                    localStorage.getItem(TABS_OPEN) != null
                      ? Number(localStorage.getItem(TABS_OPEN))
                      : 1,
                };

                if (loadTime < 60000) {
                  AccountAPI.logAccountMetricsToDataDog({
                    key: 'page_response_time',
                    value: loadTime,
                    tags: {
                      account: account.defaultDomainName,
                      url: window.location.pathname.replaceAll(/\d+/g, '*'),
                      type: tagName,
                    },
                  });
                  logger.log('page_response_time', logData);
                } else {
                  logger.log('page_response_time_skewed', logData);
                }
              };

              if (shouldInitDatadogRum()) {
                getSessionReplayLink()
                  .then((link) => {
                    sendPerformanceLog(link ?? 'No session available');
                  })
                  .catch(() => undefined);
              } else {
                sendPerformanceLog('No session available');
              }
            }
          } catch (error) {
            logger.error('ERROR_LOGGING_PERFORMANCE_METRICS', { error });
          }
        });
        clearMarksForDataDog();
        if (trackVisits) {
          enter(pathname);
        }
      }
    },
    [
      account.defaultDomainName,
      account.status,
      accountGroup,
      user,
      enter,
      pathname,
      trackVisits,
    ],
  );

  useDeepCompareEffect(() => {
    if (logType === LogType.AutoLog) {
      logEntry(tagNames);
    }
  }, [account.defaultDomainName, logEntry, logType, tagNames]);

  const loggerFunction = logType === LogType.CallbackLog ? logEntry : logVoid;

  return {
    executeLogger: () => loggerFunction(tagNames),
  };
};

const usePrevLocation = (location) => {
  const prevLocRef = useRef(location);

  useEffect(() => {
    prevLocRef.current = location;
  }, [location]);

  return prevLocRef.current;
};

export const useRouteLoadEventInitialiser = () => {
  const location = useLocationPathname();
  const prevLoc = usePrevLocation(location);

  useEffect(() => {
    const ignoredRoute = Object.keys(IgnoreRouteChangeForThese).find(
      (route) => {
        return !!matchPath(
          {
            path: route,
            end: true,
          },
          prevLoc ?? '',
        );
      },
    );

    const redirectedRoute = Object.keys(RedirectedRoutes).find((route) => {
      return !!matchPath(
        {
          path: route,
          end: true,
        },
        prevLoc ?? '',
      );
    });

    const selectedRoute = Object.keys(RouteBasedTagsMap).find((route) => {
      return !!matchPath(
        {
          path: route,
          end: true,
        },
        location,
      );
    });

    let ignoreMarker = false;
    let isRedirectedRoute = false;

    if (ignoredRoute) {
      if (IgnoreRouteChangeForThese[ignoredRoute] === selectedRoute) {
        ignoreMarker = true;
      }
    }

    if (redirectedRoute) {
      if (
        RedirectedRoutes[redirectedRoute].find(
          (route) => route === selectedRoute,
        )
      ) {
        isRedirectedRoute = true;
      }
    }

    if (!ignoreMarker && !isRedirectedRoute && selectedRoute) {
      (RouteBasedTagsMap[selectedRoute] ?? []).forEach((tag) => {
        setMarkerForDataDog(tag);
      });
    } else if (selectedRoute == null && !window.isAppLoaded) {
      window.isAppLoaded = true;
    }

    return () => {
      if (
        !Object.keys(RedirectedRoutes).find((route) => {
          return !!matchPath(
            {
              path: route,
              end: true,
            },
            location,
          );
        })
      ) {
        clearMarksForDataDog();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
};

export const useTabsOpenCounter = () => {
  useEffect(() => {
    const tabsOpen = localStorage.getItem(TABS_OPEN);

    if (tabsOpen == null) {
      localStorage.setItem(TABS_OPEN, '1');
    } else {
      localStorage.setItem(TABS_OPEN, (Number(tabsOpen) + 1).toString());
    }

    const onUnload = () => {
      const newTabCount = localStorage.getItem(TABS_OPEN);

      if (newTabCount !== null) {
        localStorage.setItem(TABS_OPEN, (Number(newTabCount) - 1).toString());
      }
    };

    window.addEventListener('beforeunload', onUnload);

    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);
};
